import { buildCsvCell } from 'src/core/utils/exportAsCsv';

import { exportApprovalSchemesAsFlatTable } from './exportApprovalSchemesAsFlatTable';
import {
  type ExportParameters,
  exportApprovalWorkflow,
} from './exportApprovalWorkflow';
import { exportApprovalWorkflowAsFlatTable } from './exportApprovalWorkflowAsFlatTable';
import { exportApprovalWorkflowAsJson } from './exportApprovalWorkflowAsJson';
import { exportApprovalWorkflowAsStructuredTable } from './exportApprovalWorkflowAsStructuredTable';
import { generateOoxmlPackage } from '../spreadsheet/ooxml';
import { buildSheet, type Table } from '../spreadsheet/spreadsheetModel';
import { buildXlsxPackage } from '../spreadsheet/xlsx';

const sanitizeFilenameBase = (name: string): string =>
  name
    .replaceAll(/[\r\n]+/g, ' ')
    // eslint-disable-next-line no-control-regex
    .replaceAll(/[<>:"/\\|?*\u0000-\u001F]+/g, '_')
    .trim();

const downloadBinaryFile = ({
  filename,
  data,
}: {
  filename: string;
  data: Blob;
}): void => {
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  // Delay object URL revoking to ensure download has started in all browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const downloadTextFile = ({
  filename,
  mimeType,
  data,
}: {
  filename: string;
  mimeType: string;
  data: string;
}): void => {
  const byteOrderMark = '\uFEFF';
  const fileBlob = new Blob([byteOrderMark + data], {
    type: `${mimeType};charset=utf-8`,
  });
  downloadBinaryFile({ filename, data: fileBlob });
};

const makeApprovalWorkflowBasename = ({
  companyName,
  suffix = '',
}: {
  companyName: string;
  suffix?: string;
}) => `${sanitizeFilenameBase(companyName)}- approval-workflow${suffix}`;

export const downloadApprovalWorkflowAsJson = (
  companyName: string,
  exportParameters: ExportParameters,
): void => {
  const workflowExport = exportApprovalWorkflow(exportParameters);
  if (!workflowExport) {
    return;
  }
  const workflowJson = exportApprovalWorkflowAsJson(
    workflowExport,
    exportParameters.t,
  );
  downloadTextFile({
    filename: `${makeApprovalWorkflowBasename({ companyName })}.json`,
    mimeType: 'application/json',
    data: JSON.stringify(workflowJson, null, 2),
  });
};

const convertTableToCsv = (table: Table): string =>
  table.rows
    .map((row) =>
      row.cells
        .map(({ content }) => buildCsvCell(`${content ?? ''}`))
        .join(','),
    )
    .join('\n');

const downloadAsCsv = (basename: string, table: Table): void => {
  downloadTextFile({
    filename: `${basename}.csv`,
    mimeType: 'text/csv',
    data: convertTableToCsv(table),
  });
};

export const downloadApprovalWorkflowAsStructuredCsv = (
  companyName: string,
  exportParameters: ExportParameters,
): void => {
  const workflowExport = exportApprovalWorkflow(exportParameters);
  if (!workflowExport) {
    return;
  }
  const tableExport = exportApprovalWorkflowAsStructuredTable(
    workflowExport,
    exportParameters.t,
  );
  downloadAsCsv(
    makeApprovalWorkflowBasename({ companyName, suffix: ' (structured)' }),
    tableExport,
  );
};

export const downloadApprovalWorkflowAsFlatXlsx = (
  companyName: string,
  exportParameters: ExportParameters,
): void => {
  const { t } = exportParameters;
  const workflowExport = exportApprovalWorkflow(exportParameters);
  if (!workflowExport) {
    return;
  }

  const workflowTable = exportApprovalWorkflowAsFlatTable(workflowExport, t);

  const schemesTable = exportApprovalSchemesAsFlatTable(
    workflowExport.approvalSchemes,
    t,
  );

  const workflowXlsx = buildXlsxPackage(
    generateOoxmlPackage([
      buildSheet({
        name: t('approvalWorkflows.export.approvalWorkflow'),
        table: workflowTable,
      }),
      buildSheet({
        name: t('approvalWorkflows.export.approvalSchemes'),
        table: schemesTable,
      }),
    ]),
  );

  downloadBinaryFile({
    filename: `${makeApprovalWorkflowBasename({
      companyName,
      suffix: ' (flat)',
    })}.xlsx`,
    data: workflowXlsx,
  });
};
